import { toNumber, get, isNil, map, isEmpty, uniq, compact, join } from 'lodash'
import { DATA_TYPES } from '@/utils/constants'

const getters = {
    getAccountCountryId: state => {
        return toNumber(get(state, 'country.id', ''))
    },

    getUserAccess: state => params => {
        const userAccess = get(state.userRights, `${params.type}`, null)
        return isNil(userAccess) ? 'none' : userAccess
    },

    getAccessToAccount: state => () => {
        return state.userRole === 'webo_admin' || (state.isActive && state.productAccountIsActive)
    },

    getDataTypes: (_state, _getters, rootState) => {
        if (isEmpty(rootState.data.customTaxonomies.customTaxonomies)) return DATA_TYPES

        const _customTaxoTypes = map(rootState.data.customTaxonomies.customTaxonomies, taxo => {
            if (taxo.taxonomy_type === 'direct') return 'data'
            return taxo.taxonomy_type
        }).sort()

        const dataType = [...DATA_TYPES]
        dataType.splice(1, 0, 'sharing') // Put in second place
        return uniq(_customTaxoTypes.concat(dataType))
    },

    // MANAGER ACCESS
    isDevAccount: state => state.accountId == process.env.VUE_APP_DEV_ACCOUNT,

    getAccessToBuilderSegment: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'manager_access' }) !== 'none')
        )
    },

    getIsAudienceCreationAllowed: (state, getters) => params => {
        if (params.mode === 'cclone') {
            return (
                ['webo_employee', 'webo_admin'].includes(state.userRole) ||
                (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'cclone_access' }) === 'write')
            )
        }
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'manager_access' }) === 'write')
        )
    },

    // Bootstrap
    getIsBootstrapAllowed: (state, getters, rootState, rootGetters) => params => {
        if (isNil(get(params, 'mode', null))) return false
        if (state.userRole === 'webo_admin') {
            return true
        }
        if (rootState.ui.settings.general.bootstrapPush) {
            if (params.mode === 'cclone') {
                return (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'cclone_access' }) === 'write') || state.userRole === 'webo_employee'
            }

            if (params.mode === 'segment') {
                if (rootGetters['ui/builder/getIsFormulaThirdPartyOnly']()) return false
                if (state.userRole === 'webo_employee' || (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'manager_access' }) === 'write')) {
                    return rootGetters['ui/builder/getFirstPartyInFormula']()
                }
                return false
            }
            return false
        }

        return false
    },

    // CCLONE ACCESS
    getAccessToCclone: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'cclone_access' }) !== 'none')
        )
    },
    getIsCcloneActionAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'cclone_access' }) === 'write')
        )
    },
    getIsCcloneReadAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'cclone_access' }) !== 'none')
        )
    },
    // MONITORING ACCESS
    getAccessToMonitoring: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'monitoring_access' }) !== 'none')
        )
    },
    getIsMonitoringActionAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'monitoring_access' }) === 'write')
        )
    },

    // SETTINGS ACCESS
    getAccessToSettings: state => () => {
        return ['webo_employee', 'webo_admin'].includes(state.userRole)
    },

    // DEM
    getAccessToDEM: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },
    getIsDEMActionAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },

    // DSP
    getAccessToDSP: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },
    getIsDSPActionAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },

    // GEORADAR
    getAccessToGeoradar: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },
    getIsGeoradarActionAllowed: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },

    // MANAGE TAXO
    getAccessToManageTaxo: (state, getters) => () => {
        return (
            ['webo_employee', 'webo_admin'].includes(state.userRole) ||
            (state.userRole === 'webo_user' && getters.getUserAccess({ type: 'settings_access' }) === 'write')
        )
    },

    // Namespaces
    getNamespacesCRM: state => () => {
        return state.namespaces.filter(n => n.type === 'CRM')
    },

    getNamespacesPatterns: state => () => {
        const patternList = join(compact(state.namespaces.map(n => n.pattern)), ',')
        return uniq(patternList.split(','))
    },

    // Create alert
    getCreateAlertsAllowed: (state, getters) => () => {
        return ['webo_employee', 'webo_admin'].includes(state.userRole) || getters.getUserAccess({ type: 'monitoring_access' }) === 'write'
    },

    getEditAlertsAllowed: (state, getters) => () => {
        return ['webo_employee', 'webo_admin'].includes(state.userRole) || getters.getUserAccess({ type: 'monitoring_access' }) === 'write'
    },

    getReadAlertsAllowed: (state, getters) => () => {
        return ['webo_employee', 'webo_admin'].includes(state.userRole) || getters.getUserAccess({ type: 'monitoring_access' }) !== 'none'
    },

    isReady: state => !state.isLoading,

    haveCustomer: state => state.customer !== null,
    getCustomer: state => state.customer
}
export default getters
